import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BusinessCard from '../components/BusinessCard';
import { getBusiness, getRelated, submitContact } from '../api';
import { getProxiedImageUrl } from '../utils/imageProxy';
import {
  Phone, Mail, Globe, MapPin, MessageCircle,
  Star, ChevronLeft, ChevronRight,
  ExternalLink, ArrowLeft, Send, CheckCircle
} from 'lucide-react';

// ══════════════════════════
// CONTACT FORM
// ══════════════════════════
function ContactForm({ business }) {
  const [form, setForm] = useState({ nome: '', email: '', telefono: '', messaggio: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.messaggio.trim()) {
      setError('Compila tutti i campi obbligatori.');
      return;
    }
    setSending(true);
    setError('');
    try {
      await submitContact({
        business_id: business._id,
        business_nome: business.nome,
        business_email: business.emails?.[0] || '',
        nome: form.nome,
        email: form.email,
        telefono: form.telefono,
        messaggio: form.messaggio,
      });
      setSent(true);
    } catch {
      setError('Errore durante l\'invio. Riprova.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        className="rounded-2xl border p-8 text-center"
        style={{ borderColor: 'var(--mi-border)', backgroundColor: '#fff' }}
      >
        <CheckCircle size={40} className="mx-auto mb-3" style={{ color: '#1F7A5A' }} />
        <h3 className="font-display text-xl mb-2" style={{ color: 'var(--mi-fg)' }}>Messaggio inviato!</h3>
        <p className="text-sm" style={{ color: 'var(--mi-text)' }}>
          La tua richiesta è stata inviata a {business.nome}. Ti risponderanno al più presto.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="contact-form"
      className="rounded-2xl border p-6"
      style={{ borderColor: 'var(--mi-border)', backgroundColor: '#fff' }}
    >
      <h2 className="font-display text-2xl mb-1" style={{ color: 'var(--mi-fg)' }}>Contatta {business.nome}</h2>
      <p className="text-sm mb-5" style={{ color: 'var(--mi-text)' }}>Invia un messaggio direttamente all'attività.</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest block mb-1" style={{ color: 'var(--mi-text)' }}>Nome *</label>
            <input
              value={form.nome}
              onChange={e => update('nome', e.target.value)}
              placeholder="Il tuo nome"
              className="w-full h-10 px-3 rounded-xl border text-sm"
              style={{ borderColor: 'var(--mi-border)', backgroundColor: 'var(--mi-bg)', outline: 'none', color: 'var(--mi-fg)' }}
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest block mb-1" style={{ color: 'var(--mi-text)' }}>Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={e => update('email', e.target.value)}
              placeholder="tua@email.it"
              className="w-full h-10 px-3 rounded-xl border text-sm"
              style={{ borderColor: 'var(--mi-border)', backgroundColor: 'var(--mi-bg)', outline: 'none', color: 'var(--mi-fg)' }}
              required
            />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest block mb-1" style={{ color: 'var(--mi-text)' }}>Telefono (opzionale)</label>
          <input
            value={form.telefono}
            onChange={e => update('telefono', e.target.value)}
            placeholder="+39 000 0000000"
            className="w-full h-10 px-3 rounded-xl border text-sm"
            style={{ borderColor: 'var(--mi-border)', backgroundColor: 'var(--mi-bg)', outline: 'none', color: 'var(--mi-fg)' }}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest block mb-1" style={{ color: 'var(--mi-text)' }}>Messaggio *</label>
          <textarea
            value={form.messaggio}
            onChange={e => update('messaggio', e.target.value)}
            placeholder="Scrivi qui la tua richiesta o domanda..."
            rows={4}
            className="w-full px-3 py-2 rounded-xl border text-sm resize-none"
            style={{ borderColor: 'var(--mi-border)', backgroundColor: 'var(--mi-bg)', outline: 'none', color: 'var(--mi-fg)' }}
            required
          />
        </div>
        {error && <p className="text-sm" style={{ color: '#B42318' }}>{error}</p>}
        <button
          type="submit"
          data-testid="contact-form-submit"
          disabled={sending}
          className="inline-flex items-center gap-2 px-6 h-11 rounded-xl text-sm font-medium transition-colors"
          style={{ backgroundColor: 'var(--mi-fg)', color: '#fff', opacity: sending ? 0.7 : 1 }}
        >
          <Send size={14} />
          {sending ? 'Invio in corso...' : 'Invia messaggio'}
        </button>
      </form>
    </div>
  );
}

// ══════════════════════════
// MAIN COMPONENT
// ══════════════════════════
export default function BusinessDetailPage() {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setImgIndex(0);
    getBusiness(slug)
      .then(r => {
        setBusiness(r.data);
        getRelated(slug).then(rr => setRelated(rr.data)).catch(() => {});
      })
      .catch(() => setError('Attività non trovata'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div style={{ backgroundColor: 'var(--mi-bg)' }}>
      <Header />
      <div className="mi-container py-10">
        <div className="skeleton-shimmer rounded-2xl" style={{ height: '400px' }} />
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <div className="skeleton-shimmer h-8 rounded w-2/3" />
            <div className="skeleton-shimmer h-4 rounded" />
            <div className="skeleton-shimmer h-4 rounded w-5/6" />
          </div>
          <div className="space-y-4">
            <div className="skeleton-shimmer h-40 rounded-2xl" />
          </div>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ backgroundColor: 'var(--mi-bg)' }}>
      <Header />
      <div className="mi-container py-24 text-center">
        <p className="font-display text-2xl mb-2" style={{ color: 'var(--mi-fg)' }}>{error}</p>
        <Link to="/attivita" className="text-sm hover:underline" style={{ color: 'var(--mi-accent)' }}>Torna all'archivio</Link>
      </div>
      <Footer />
    </div>
  );

  if (!business) return null;

  const images = business.immagini?.filter(u => u && u.startsWith('http')).map(u => getProxiedImageUrl(u)) || [];
  const hasImages = images.length > 0;
  const mainImg = hasImages ? images[imgIndex] : null;

  const locationDisplay = (() => {
    const { citta, provincia, provincia_codice } = business;
    if (citta && provincia && citta !== provincia) return `${citta} (${provincia_codice || provincia})`;
    if (citta) return citta;
    if (provincia) return provincia;
    return '';
  })();

  return (
    <div style={{ backgroundColor: 'var(--mi-bg)' }} data-testid="business-detail">
      <Header />

      {/* HERO GALLERY */}
      <div className="mi-container mt-6">
        <div
          data-testid="business-hero-gallery"
          className="relative rounded-2xl overflow-hidden"
          style={{ paddingTop: hasImages ? '45%' : '30%', backgroundColor: 'var(--mi-muted)' }}
        >
          {mainImg ? (
            <img
              src={mainImg}
              alt={`${business.nome} - immagine ${imgIndex + 1}`}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-display text-6xl" style={{ color: 'var(--mi-border)' }}>✦</span>
            </div>
          )}
          {/* Gradient */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(26,26,26,0.65) 0%, transparent 55%)' }} />

          {/* Business name on hero */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-end gap-4">
              {business.logo && (
                <div className="w-16 h-16 rounded-xl border-2 overflow-hidden bg-white flex-shrink-0" style={{ borderColor: 'rgba(255,255,255,0.5)' }}>
                  <img src={business.logo} alt={`Logo ${business.nome}`} className="w-full h-full object-contain p-1" />
                </div>
              )}
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  {business.categoria && (
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.25)', color: '#fff' }}>
                      {business.categoria}
                    </span>
                  )}
                  {business.featured && (
                    <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: 'var(--mi-accent)', color: '#fff' }}>
                      <Star size={10} fill="currentColor" /> In evidenza
                    </span>
                  )}
                </div>
                <h1 className="font-display text-2xl md:text-3xl lg:text-4xl text-white tracking-tight leading-tight">
                  {business.nome}
                </h1>
                {locationDisplay && (
                  <p className="flex items-center gap-1 mt-1 text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    <MapPin size={12} /> {locationDisplay}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Gallery navigation */}
          {images.length > 1 && (
            <>
              <button
                onClick={() => setImgIndex((imgIndex - 1 + images.length) % images.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.8)', color: 'var(--mi-fg)' }}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setImgIndex((imgIndex + 1) % images.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.8)', color: 'var(--mi-fg)' }}
              >
                <ChevronRight size={16} />
              </button>
              <div className="absolute bottom-24 right-4 flex gap-1">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIndex(i)}
                    className="rounded-full transition-all"
                    style={{ width: i === imgIndex ? '20px' : '6px', height: '6px', backgroundColor: i === imgIndex ? '#fff' : 'rgba(255,255,255,0.5)' }}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Thumbnail strip */}
        {images.length > 1 && (
          <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setImgIndex(i)}
                className="flex-shrink-0 rounded-lg overflow-hidden border-2 transition-colors"
                style={{ width: '64px', height: '48px', borderColor: i === imgIndex ? 'var(--mi-fg)' : 'transparent' }}
              >
                <img 
                  src={img} 
                  alt={`${i + 1}`} 
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* MAIN CONTENT */}
      <div className="mi-container mt-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* LEFT COLUMN */}
          <div className="lg:col-span-8 space-y-8">

            {/* Quick action buttons */}
            <div className="flex flex-wrap gap-3">
              {business.url && (
                <a href={business.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-medium"
                  style={{ backgroundColor: 'var(--mi-fg)', color: 'var(--mi-bg)' }}
                >
                  <Globe size={14} /> Sito web <ExternalLink size={12} />
                </a>
              )}
              {business.whatsapp && (
                <a href={`https://wa.me/${business.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-medium border"
                  style={{ borderColor: '#25D366', color: '#25D366', backgroundColor: '#fff' }}
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
              )}
              {business.telefoni?.[0] && (
                <a href={`tel:${business.telefoni[0]}`}
                  className="inline-flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-medium border"
                  style={{ borderColor: 'var(--mi-border)', color: 'var(--mi-fg)', backgroundColor: '#fff' }}
                >
                  <Phone size={14} /> {business.telefoni[0]}
                </a>
              )}
            </div>

            {/* Summary box */}
            {business.payoff && (
              <div
                data-testid="business-summary"
                className="rounded-2xl p-6 border"
                style={{ backgroundColor: 'var(--mi-muted)', borderColor: 'var(--mi-border)' }}
              >
                <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--mi-accent)' }}>In breve</p>
                <p className="text-base leading-relaxed font-display italic" style={{ color: 'var(--mi-fg)' }}>“{business.payoff}”</p>
              </div>
            )}

            {/* Descrizione */}
            {business.descrizione && (
              <section data-testid="business-section-offerta">
                <h2 className="font-display text-2xl mb-3" style={{ color: 'var(--mi-fg)' }}>Cosa offre</h2>
                <p className="text-base leading-relaxed" style={{ color: 'var(--mi-text)' }}>{business.descrizione}</p>
              </section>
            )}

            {/* Servizi */}
            {business.servizi && (
              <section>
                <h2 className="font-display text-2xl mb-3" style={{ color: 'var(--mi-fg)' }}>Servizi</h2>
                <div className="p-5 rounded-2xl border" style={{ borderColor: 'var(--mi-border)', backgroundColor: '#fff' }}>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--mi-text)' }}>{business.servizi}</p>
                </div>
              </section>
            )}

            {/* Per chi è adatta */}
            {business.target && (
              <section data-testid="business-section-per-chi">
                <h2 className="font-display text-2xl mb-3" style={{ color: 'var(--mi-fg)' }}>Per chi è adatta</h2>
                <p className="text-base leading-relaxed" style={{ color: 'var(--mi-text)' }}>{business.target}</p>
              </section>
            )}

            {/* Specializzazioni */}
            {business.sottocategorie?.length > 0 && (
              <section>
                <h2 className="font-display text-2xl mb-3" style={{ color: 'var(--mi-fg)' }}>Specializzazioni</h2>
                <div className="flex flex-wrap gap-2">
                  {business.sottocategorie.map((s, i) => (
                    <span key={i} className="text-sm px-3 py-1.5 rounded-full border"
                      style={{ borderColor: 'var(--mi-border)', backgroundColor: '#fff', color: 'var(--mi-fg)' }}
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Argomenti correlati */}
            {business.keywords?.length > 0 && (
              <section>
                <h2 className="font-display text-xl mb-3" style={{ color: 'var(--mi-fg)' }}>Argomenti correlati</h2>
                <div className="flex flex-wrap gap-2">
                  {business.keywords.slice(0, 15).map((k, i) => (
                    <Link key={i} to={`/attivita?q=${encodeURIComponent(k)}`}
                      className="text-xs px-2 py-1 rounded-full border transition-colors hover:border-[var(--mi-accent)]"
                      style={{ borderColor: 'var(--mi-border)', color: 'var(--mi-text)', backgroundColor: 'var(--mi-muted)' }}
                    >
                      {k}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* CONTACT FORM */}
            <ContactForm business={business} />
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="lg:col-span-4 space-y-5">
            {/* Contacts */}
            <div
              data-testid="business-section-contatti"
              className="rounded-2xl border p-5"
              style={{ borderColor: 'var(--mi-border)', backgroundColor: '#fff' }}
            >
              <h3 className="font-display text-xl mb-4" style={{ color: 'var(--mi-fg)' }}>Contatti</h3>
              <div className="space-y-3">
                {business.telefoni?.map((t, i) => (
                  <a key={i} href={`tel:${t}`} className="flex items-center gap-3 text-sm hover:underline" style={{ color: 'var(--mi-fg)' }}>
                    <Phone size={15} style={{ color: 'var(--mi-accent)' }} /> {t}
                  </a>
                ))}
                {business.emails?.map((e, i) => (
                  <a key={i} href={`mailto:${e}`} className="flex items-center gap-3 text-sm hover:underline break-all" style={{ color: 'var(--mi-fg)' }}>
                    <Mail size={15} style={{ color: 'var(--mi-accent)' }} /> {e}
                  </a>
                ))}
                {business.url && (
                  <a href={business.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 text-sm hover:underline break-all"
                    style={{ color: 'var(--mi-fg)' }}
                  >
                    <Globe size={15} style={{ color: 'var(--mi-accent)' }} />
                    {business.sito || business.url}
                  </a>
                )}
              </div>
            </div>

            {/* Address + Google Maps */}
            {business.indirizzo && (
              <div
                data-testid="business-section-dove"
                className="rounded-2xl border overflow-hidden"
                style={{ borderColor: 'var(--mi-border)', backgroundColor: '#fff' }}
              >
                <div className="p-5 pb-3">
                  <h3 className="font-display text-xl mb-2" style={{ color: 'var(--mi-fg)' }}>Dove si trova</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--mi-text)' }}>{business.indirizzo}</p>
                  <a
                    href={`https://www.google.com/maps/search/${encodeURIComponent(business.indirizzo)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium hover:underline"
                    style={{ color: 'var(--mi-accent)' }}
                  >
                    <MapPin size={12} /> Apri in Google Maps
                  </a>
                </div>
                {/* Google Maps embed */}
                <div className="w-full" style={{ height: '220px' }}>
                  <iframe
                    title={`Mappa di ${business.nome}`}
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(business.indirizzo)}&output=embed&z=15`}
                    width="100%"
                    height="220"
                    style={{ border: 0, display: 'block' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            )}

            {/* Social */}
            {business.social && Object.keys(business.social).length > 0 && (
              <div className="rounded-2xl border p-5" style={{ borderColor: 'var(--mi-border)', backgroundColor: '#fff' }}>
                <h3 className="font-display text-xl mb-3" style={{ color: 'var(--mi-fg)' }}>Social</h3>
                <div className="flex flex-wrap gap-2">
                  {business.social.facebook && (
                    <a href={business.social.facebook} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg border text-xs font-bold hover:bg-blue-50"
                      style={{ borderColor: 'var(--mi-border)', color: '#1877F2' }}
                    >Facebook</a>
                  )}
                  {business.social.instagram && (
                    <a href={business.social.instagram} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg border text-xs font-bold hover:bg-pink-50"
                      style={{ borderColor: 'var(--mi-border)', color: '#E4405F' }}
                    >Instagram</a>
                  )}
                  {business.social.x && (
                    <a href={business.social.x} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg border text-xs font-bold hover:bg-gray-100"
                      style={{ borderColor: 'var(--mi-border)', color: '#1A1A1A' }}
                    >X / Twitter</a>
                  )}
                  {business.social.tripadvisor && (
                    <a href={business.social.tripadvisor} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg border text-xs font-bold hover:bg-green-50"
                      style={{ borderColor: 'var(--mi-border)', color: '#00AA6C' }}
                    >TripAdvisor</a>
                  )}
                  {business.social.tiktok && (
                    <a href={business.social.tiktok} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg border text-xs font-bold hover:bg-gray-100"
                      style={{ borderColor: 'var(--mi-border)', color: 'var(--mi-fg)' }}
                    >TikTok</a>
                  )}
                  {business.social.booking && (
                    <a href={business.social.booking} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg border text-xs font-bold"
                      style={{ borderColor: 'var(--mi-border)', color: '#003B95' }}
                    >Booking</a>
                  )}
                </div>
              </div>
            )}

            {/* Back link */}
            <Link to="/attivita" className="flex items-center gap-2 text-sm hover:underline" style={{ color: 'var(--mi-text)' }}>
              <ArrowLeft size={14} /> Torna all'archivio
            </Link>
          </div>
        </div>

        {/* RELATED */}
        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="font-display text-3xl mb-6" style={{ color: 'var(--mi-fg)' }}>Attività simili</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((b) => (
                <BusinessCard key={b._id} business={b} />
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
